import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Row, Col, Space, Radio, Alert } from 'antd';
import { BankOutlined, TeamOutlined, UserOutlined, MailOutlined, LockOutlined, PhoneOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import './Register.css';

const { Title, Text, Paragraph } = Typography;

interface RegistrationForm {
  tenant_type: 'hr_consultant' | 'direct_client';
  company_name: string;
  admin_email: string;
  admin_password: string;
  confirm_password: string;
  phone?: string;
  country: string;
}

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [tenantType, setTenantType] = useState<'hr_consultant' | 'direct_client'>('direct_client');

  const registrationOptions = {
    hr_consultant: {
      title: 'HR Consulting Organization',
      subtitle: 'Manage multiple client companies',
      description: 'Perfect for HR consultants managing multiple client organizations. Get 10 days free trial with up to 50 employees across all clients.',
      trialDays: 10,
      trialEmployeeLimit: 50,
      features: [
        'Manage unlimited client companies',
        'Separate data isolation per client',
        'Consolidated reporting and analytics',
        'White-label client access',
        'Advanced multi-tenant controls'
      ],
      icon: <TeamOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      color: '#1890ff'
    },
    direct_client: {
      title: 'Individual Organization',
      subtitle: 'Direct HR management for your company',
      description: 'Perfect for individual companies managing their own HR processes. Get 14 days free trial with up to 25 employees.',
      trialDays: 14,
      trialEmployeeLimit: 25,
      features: [
        'Complete HR management system',
        'Employee self-service portal',
        'Payroll and benefits management',
        'Performance tracking',
        'Document management'
      ],
      icon: <BankOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      color: '#52c41a'
    }
  };

  const onFinish = async (values: RegistrationForm) => {
    try {
      setError('');
      setLoading(true);
      
      // Add trial dates based on tenant type
      const trialDays = registrationOptions[values.tenant_type].trialDays;
      const trialStartDate = new Date();
      const trialEndDate = new Date();
      trialEndDate.setDate(trialStartDate.getDate() + trialDays);

      const registrationData = {
        ...values,
        trial_start_date: trialStartDate.toISOString(),
        trial_end_date: trialEndDate.toISOString(),
        trial_employee_limit: registrationOptions[values.tenant_type].trialEmployeeLimit,
        is_trial: true,
        status: 'trial',
        plan: 'trial'
      };

      await authService.register(registrationData);
      
      // Redirect to login with success message
      navigate('/login', { 
        state: { 
          message: `Registration successful! Your ${trialDays}-day trial has started.`,
          type: 'success'
        }
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentOption = registrationOptions[tenantType];

  return (
    <div className="register-container">
      <Row justify="center" align="middle" style={{ minHeight: '100vh', padding: '20px 0' }}>
        <Col xs={22} sm={20} md={16} lg={12} xl={10}>
          <Card className="register-card" bordered={false}>
            {/* Header */}
            <div className="register-header">
              <div className="logo">
                <BankOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              </div>
              <Title level={2} className="register-title">Join Nexus HR System</Title>
              <Text type="secondary" className="register-subtitle">
                Choose your registration type and start your free trial
              </Text>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert
                message="Registration Failed"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              initialValues={{ tenant_type: 'direct_client', country: 'Nigeria' }}
            >
              {/* Registration Type Selection */}
              <Form.Item
                name="tenant_type"
                label="Registration Type"
                rules={[{ required: true, message: 'Please select registration type' }]}
              >
                <Radio.Group 
                  onChange={(e) => setTenantType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio.Button 
                      value="direct_client" 
                      style={{ 
                        height: 'auto', 
                        padding: '16px',
                        width: '100%',
                        textAlign: 'left',
                        borderColor: tenantType === 'direct_client' ? '#52c41a' : '#d9d9d9'
                      }}
                    >
                      <Space>
                        <BankOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            Individual Organization
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>
                            14-day trial • Up to 25 employees • N5,000/$10 per employee/month
                          </div>
                        </div>
                      </Space>
                    </Radio.Button>
                    
                    <Radio.Button 
                      value="hr_consultant" 
                      style={{ 
                        height: 'auto', 
                        padding: '16px',
                        width: '100%',
                        textAlign: 'left',
                        borderColor: tenantType === 'hr_consultant' ? '#1890ff' : '#d9d9d9'
                      }}
                    >
                      <Space>
                        <TeamOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            HR Consulting Organization
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>
                            10-day trial • Up to 50 employees • Manage multiple clients
                          </div>
                        </div>
                      </Space>
                    </Radio.Button>
                  </Space>
                </Radio.Group>
              </Form.Item>

              {/* Selected Option Details */}
              <Card 
                size="small" 
                style={{ 
                  marginBottom: 24, 
                  borderColor: currentOption.color,
                  backgroundColor: `${currentOption.color}08`
                }}
              >
                <Space>
                  {currentOption.icon}
                  <div>
                    <Title level={4} style={{ margin: 0, color: currentOption.color }}>
                      {currentOption.title}
                    </Title>
                    <Paragraph style={{ margin: 0, color: '#666' }}>
                      {currentOption.description}
                    </Paragraph>
                  </div>
                </Space>
              </Card>

              {/* Company Information */}
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Company Name"
                    name="company_name"
                    rules={[
                      { required: true, message: 'Please enter your company name' },
                      { min: 2, message: 'Company name must be at least 2 characters' }
                    ]}
                  >
                    <Input
                      prefix={<BankOutlined />}
                      placeholder="Enter your company name"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    label="Admin Email"
                    name="admin_email"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined />}
                      placeholder="admin@company.com"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Country"
                    name="country"
                    rules={[{ required: true, message: 'Please select country' }]}
                  >
                    <Input
                      prefix={<GlobalOutlined />}
                      placeholder="Nigeria"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="admin_password"
                    rules={[
                      { required: true, message: 'Please enter password' },
                      { min: 8, message: 'Password must be at least 8 characters' }
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Create password"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Confirm Password"
                    name="confirm_password"
                    dependencies={['admin_password']}
                    rules={[
                      { required: true, message: 'Please confirm password' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('admin_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Passwords do not match'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm password"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ backgroundColor: currentOption.color, borderColor: currentOption.color }}
                >
                  {loading ? 'Creating Account...' : `Start ${currentOption.trialDays}-Day Free Trial`}
                </Button>
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Text type="secondary">
                  Already have an account?{' '}
                  <Button type="link" onClick={() => navigate('/login')} style={{ padding: 0 }}>
                    Sign In
                  </Button>
                </Text>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
